<template :open-modal="openModal">
  <slot name="trigger" :open-modal="openModal"></slot>
  <!-- No need to use teleport as headlessUI is already using it under the hood -->
  <TransitionRoot appear :show="isOpen || setIsOpen" as="template">
    <Dialog as="div" @close="closeModal">
      <div class="fixed inset-0 z-40 overflow-y-auto">
        <div class="min-h-screen px-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-black/30 backdrop-blur dark:bg-black/60" />
          </TransitionChild>

          <span class="inline-block h-screen align-middle" aria-hidden="true"> &#8203; </span>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="my-8 inline-block w-full transform overflow-hidden rounded-lg bg-yapgray-100 text-left align-middle shadow-xl transition-all dark:border dark:border-yapdarkgray-500 dark:bg-yapdarkgray-900"
              :class="{
                'max-w-7xl': extraWide,
                'max-w-5xl': wide,
                'max-w-md': !extraWide && !wide
              }"
            >
              <div v-if="unstyled" class="relative">
                <YapButtonIcon class="absolute right-2 top-2" data-testid="close-modal-button" @click="closeModal">
                  <YapIcon name="close-outline" class="h-6 w-6" />
                </YapButtonIcon>
              </div>
              <div
                v-else
                class="flex flex-shrink-0 items-center justify-between rounded-t-md px-4 py-2"
                :class="{ 'border-b border-yapgray-300 dark:border-yapdarkgray-600': !!$slots.title }"
              >
                <DialogTitle>
                  <slot name="title" v-if="!!$slots.title" />
                </DialogTitle>
                <YapButtonIcon @click="closeModal" data-testid="close-modal-button">
                  <YapIcon name="close-outline" class="h-6 w-6" />
                </YapButtonIcon>
              </div>
              <div class="max-h-fit overflow-y-auto" :class="{ 'p-3 dark:text-yapgray-100': !unstyled }">
                <slot :close-modal="closeModal" />
              </div>
              <div
                class="flex justify-end gap-3 border-t bg-yapgray-100 p-2 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-900"
                v-if="!!$slots.footer"
              >
                <slot name="footer" :close-modal="closeModal" />
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { TransitionRoot, TransitionChild, Dialog, DialogOverlay, DialogTitle } from '@headlessui/vue';

import YapIcon from '@/components/YapIcon.vue';
import YapButtonIcon from '@/components/YapButtonIcon.vue';

const props = defineProps<{
  wide?: boolean;
  extraWide?: boolean;
  setIsOpen?: boolean;
  unstyled?: boolean;
}>();
const emit = defineEmits(['modalClosed']);

const isOpen = ref(false);

function closeModal() {
  isOpen.value = false;
  emit('modalClosed');
}
function openModal() {
  isOpen.value = true;
}

watch(
  () => props.setIsOpen,
  (newVal, oldVal) => {
    if (newVal !== undefined && newVal !== oldVal) {
      isOpen.value = newVal;
    }
  }
);
</script>
